import {
  Box,
  InputElementProps,
  InputGroup,
  InputGroupProps,
  InputProps,
  InputRightElement,
  useDisclosure
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { FieldAttributes } from "formik"
import { createContext, useContext, useState } from "react"

import { useSize } from "../../utils/size"
import Tooltip, { TooltipProps } from "../tooltip/Tooltip"
import Field, { FieldChildren } from "./Field"

// Determine when the tip opens and closes.
// - auto: opens on focus, closes on blur
// - manual: opens on click, closes on click or blur
type TippedFieldMode = "auto" | "manual"

type Tip = {
  icon: IconName
  activeIcon?: IconName
  placeholder?: string
  mode?: TippedFieldMode
}

export type FieldTip = Tip & Omit<TooltipProps, "children">

export function useTippedField(tip: FieldTip) {
  const { icon, activeIcon, placeholder, mode, ...tooltipProps } = tip
  const [nodeRef, width] = useSize("width")
  const [tipMode, setTipMode] = useState<TippedFieldMode>(mode || "manual")

  const disclosure = useDisclosure()
  const { isOpen, onOpen, onClose } = disclosure

  function handleInputFocus() {
    // Only auto-open in auto mode
    if (tipMode === "auto" && !isOpen) {
      onOpen()
    }
  }

  function handleInputBlur() {
    // // Only auto-close in auto mode
    // if (tipMode === "auto" && isOpen) {
    //   onClose()
    // }
    // For now, always close on blur.
    // If we wish to require manual closure for auto mode, we must
    // hook up a click handler to the tooltip trigger (or wrapping component,
    // which was attempted with TippedFieldRightInputElement but didn't work
    // due to the Input not receiving the inlin padding since we were wrapping
    // the InputRightElement).
    if (isOpen) {
      onClose()
      // If we were in auto mode, switch to manual mode.
      if (tipMode === "auto") {
        setTipMode("manual")
      }
    }
  }

  function getInputProps(props: InputProps): InputProps {
    // Using the tip placeholder when the tooltip is open.
    if (isOpen && placeholder) {
      return {
        ...props,
        placeholder: placeholder
      }
    }

    if (typeof props.placeholder === "undefined") {
      // Omit placeholder if undefined to prevent overriding
      // the prop downstream.
      const { placeholder, ...rest } = props
      return rest
    }

    return props
  }

  function getInputGroupProps(
    props?: InputGroupProps
  ): InputGroupProps & { ref: React.Ref<HTMLElement> } {
    return {
      variant: "filled",
      onFocus: handleInputFocus,
      onBlur: handleInputBlur,
      ref: nodeRef,
      sx: {
        ...props?.sx,
        // For ensuring tooltip is positioned relative to the input element.
        ".chakra-input__left-element .TooltipWrapper, .chakra-input__right-element .TooltipWrapper":
          {
            pos: "absolute",
            inset: 0
          }
      },
      ...props
    }
  }

  function getTooltipProps(
    props?: Partial<TooltipProps>
  ): Omit<TooltipProps, "children"> {
    return {
      ...tooltipProps,
      ...props,
      popover: {
        placement: "bottom-end",
        // Only auto-focus the tooltip when in manual mode.
        // We don't want to auto-focus the tooltip when in auto mode since
        // this removes focus from the input (to the popover), and results
        // in auto-closing the tooltip (and other unwanted side effects).
        autoFocus: tipMode === "manual",
        ...tooltipProps?.popover,
        ...props?.popover
      },
      content: {
        width: `${width}px`,
        display: width ? undefined : "none",
        ...tooltipProps?.content,
        ...props?.content
      },
      disclosure
    }
  }

  function getInputElementProps(props?: InputElementProps): InputElementProps {
    return {
      ...props,
      onClick: () => {
        // Manually closing tooltip, so user exits auto mode.
        if (tipMode === "auto") {
          setTipMode("manual")
        }
      }
    }
  }

  function getIconProps(
    props?: Partial<React.ComponentProps<typeof Pathicon>>
  ): React.ComponentProps<typeof Pathicon> {
    return {
      ...props,
      icon: isOpen && activeIcon ? activeIcon : icon,
      size: "1em"
    }
  }

  return {
    getInputProps,
    getInputGroupProps,
    getTooltipProps,
    // getInputElementProps,
    getIconProps
  }
}

// TippedField Context
type TippedFieldContextValue = ReturnType<typeof useTippedField>

const TippedFieldContext = createContext<TippedFieldContextValue | null>(null)

export const useTippedFieldContext = () => {
  const context = useContext(TippedFieldContext)
  if (!context) {
    throw new Error(
      "useTippedFieldContext must be used within a TippedFieldProvider"
    )
  }
  return context
}

interface TippedFieldProviderProps {
  tip: FieldTip
  children:
    | React.ReactNode
    | ((value: TippedFieldContextValue) => React.ReactNode)
}

const TippedFieldProvider: React.FC<TippedFieldProviderProps> = ({
  tip,
  children
}) => {
  const value = useTippedField(tip)

  return (
    <TippedFieldContext.Provider value={value}>
      {typeof children === "function" ? children(value) : children}
    </TippedFieldContext.Provider>
  )
}

// TippedField InputGroup
const TippedFieldInputGroup: React.FC<InputGroupProps> = ({
  children,
  ...props
}) => {
  const { getInputGroupProps } = useTippedFieldContext()

  return <InputGroup {...getInputGroupProps(props)}>{children}</InputGroup>
}

// TippedField Tooltip
const TippedFieldTooltip: React.FC<Partial<TooltipProps>> = ({
  children,
  ...props
}) => {
  const { getTooltipProps } = useTippedFieldContext()

  return <Tooltip {...getTooltipProps(props)}>{children}</Tooltip>
}

// // TippedField InputRightElement
// const TippedFieldRightInputElement = forwardRef<
//   HTMLDivElement,
//   InputElementProps
// >(({ children, ...props }, ref) => {
//   const { getInputElementProps } = useTippedFieldContext()

//   return (
//     <InputRightElement ref={ref} {...getInputElementProps({ ...props })}>
//       {children}
//     </InputRightElement>
//   )
// })

// // TippedField InputLeftElement
// const TippedFieldLeftInputElement: React.FC<InputElementProps> = ({
//   children,
//   ...props
// }) => {
//   const { getInputElementProps } = useTippedFieldContext()

//   return (
//     <InputLeftElement {...getInputElementProps(props)}>
//       {children}
//     </InputLeftElement>
//   )
// }

// TippedField Icon
const TippedFieldIcon: React.FC<
  Partial<React.ComponentProps<typeof Pathicon>>
> = (props) => {
  const { getIconProps } = useTippedFieldContext()

  return (
    // Wrapping with Box for ensuring tooltip is positioned relative to the input element.
    <Box
      pos="absolute"
      inset={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Pathicon {...getIconProps(props)} />
    </Box>
  )
}

// Main TippedField component using the context pattern, may just be helpful
// as an example.
export function TippedField<
  TFieldProps extends "as" extends keyof FieldAttributes<any>
    ? { placeholder?: InputProps["placeholder"] }
    : InputProps
>({
  children,
  tip,
  ...props
}: FieldAttributes<any> &
  TFieldProps & { tip: FieldTip; children?: FieldChildren }): JSX.Element {
  return (
    <TippedFieldProvider tip={tip}>
      {({ getInputProps }) => (
        <Field {...props} {...getInputProps(props)}>
          {({ field }) => (
            <TippedFieldInputGroup>
              {field}
              {!!tip && (
                <InputRightElement minH="100%">
                  <TippedFieldTooltip {...tip}>
                    <TippedFieldIcon />
                  </TippedFieldTooltip>
                </InputRightElement>
              )}
            </TippedFieldInputGroup>
          )}
        </Field>
      )}
    </TippedFieldProvider>
  )
}

// Export the compound component pattern
export default {
  Provider: TippedFieldProvider,
  InputGroup: TippedFieldInputGroup,
  Tooltip: TippedFieldTooltip,
  // InputRightElement: TippedFieldRightInputElement,
  // InputLeftElement: TippedFieldLeftInputElement,
  Icon: TippedFieldIcon
}
